import { useQuery } from '@apollo/client';
import { useEffect, useRef, useState } from 'react';

import type { UserFormDataType } from '@xing-com/crate-jobs-domain-apply-types';
import { GetUserEmailWithPhoneFallbackDocument } from '@xing-com/crate-jobs-graphql';
import { useViewerData } from '@xing-com/hub';

import { getUserPhoneData } from './get-user-phone-data';

const USER_APPLICATIONS_PAGE_SIZE = 99;
const MAX_USER_APPLICATIONS_PAGES_TO_SCAN = 3;

type UserApplicationEdge = {
  node: {
    createdAt: string;
    formDataPhone?: string | null;
  };
};

type UserApplications = {
  edges?: Array<UserApplicationEdge | null> | null;
  pageInfo?: {
    endCursor?: string | null;
    hasNextPage: boolean;
  } | null;
};

const getLatestApplicationPhone = (
  edges: Array<UserApplicationEdge | null> | null | undefined
): string | null | undefined => {
  const latestApplicationWithPhone = edges?.reduce<
    UserApplicationEdge['node'] | undefined
  >((latest, edge) => {
    const node = edge?.node;
    if (!node?.formDataPhone) return latest;
    if (!latest || Date.parse(node.createdAt) > Date.parse(latest.createdAt)) {
      return node;
    }
    return latest;
  }, undefined);

  return latestApplicationWithPhone?.formDataPhone;
};

type UseUserEmailWithPhoneFallbackValue = {
  isUserEmailError: boolean;
  isUserEmailLoading: boolean;
  userFormData: UserFormDataType;
};

export const useUserEmailWithPhoneFallback = (
  isLoggedIn: boolean
): UseUserEmailWithPhoneFallbackValue => {
  const { loading: isViewerDataLoading, data: viewerData } = useViewerData();
  const userId = viewerData?.user?.id;
  const [previousApplicationPhone, setPreviousApplicationPhone] = useState<
    string | null | undefined
  >();
  const isFetchingApplicationPages = useRef(false);
  const { error, loading, data, fetchMore } = useQuery(
    GetUserEmailWithPhoneFallbackDocument,
    {
      errorPolicy: 'all',
      fetchPolicy: 'network-only',
      skip: !isLoggedIn || !userId,
      variables: { userId: userId ?? '0', first: USER_APPLICATIONS_PAGE_SIZE },
    }
  );
  const userApplications = data?.viewer?.userApplications as
    | UserApplications
    | null
    | undefined;
  const isApplicationPhonePaginationLoading =
    !loading &&
    previousApplicationPhone === undefined &&
    !getLatestApplicationPhone(userApplications?.edges) &&
    !!userApplications?.pageInfo?.hasNextPage &&
    !!userApplications.pageInfo.endCursor;

  useEffect(() => {
    setPreviousApplicationPhone(undefined);
    isFetchingApplicationPages.current = false;
  }, [userId]);

  useEffect(() => {
    const phoneFromFirstPage = getLatestApplicationPhone(
      userApplications?.edges
    );
    const endCursor = userApplications?.pageInfo?.endCursor;

    if (
      !userId ||
      !userApplications?.pageInfo?.hasNextPage ||
      !endCursor ||
      phoneFromFirstPage ||
      isFetchingApplicationPages.current
    ) {
      return;
    }

    isFetchingApplicationPages.current = true;
    let isCancelled = false;

    const findPreviousApplicationPhone = async (): Promise<void> => {
      let applicationPage: UserApplications | null | undefined =
        userApplications;
      let applicationPhone = phoneFromFirstPage;
      let pagesScanned = 1;

      try {
        while (
          !applicationPhone &&
          pagesScanned < MAX_USER_APPLICATIONS_PAGES_TO_SCAN &&
          applicationPage?.pageInfo?.hasNextPage &&
          applicationPage.pageInfo.endCursor
        ) {
          const { data: nextPageData } = await fetchMore({
            variables: {
              after: applicationPage.pageInfo.endCursor,
              first: USER_APPLICATIONS_PAGE_SIZE,
              userId,
            },
          });
          pagesScanned += 1;
          applicationPage = nextPageData.viewer?.userApplications as
            | UserApplications
            | null
            | undefined;
          applicationPhone = getLatestApplicationPhone(applicationPage?.edges);
        }
      } catch {
        applicationPhone = null;
      }

      if (!isCancelled) {
        setPreviousApplicationPhone(applicationPhone ?? null);
      }
      isFetchingApplicationPages.current = false;
    };

    void findPreviousApplicationPhone();

    return () => {
      isCancelled = true;
    };
  }, [fetchMore, userApplications, userId]);
  const accountSettings = data?.viewer?.accountSettings;
  const privateContactDetails =
    data?.profileModules?.xingIdModule?.contactDetails?.private;
  const userEmail = accountSettings?.email?.active;
  const userPhone = getUserPhoneData(
    accountSettings?.phone?.active,
    privateContactDetails,
    previousApplicationPhone ??
      getLatestApplicationPhone(userApplications?.edges)
  );
  const hasOnlyUserApplicationsErrors =
    !!error?.graphQLErrors.length &&
    error.graphQLErrors.every(({ path }) => path?.includes('userApplications'));

  return {
    isUserEmailError:
      (!!error && !hasOnlyUserApplicationsErrors) ||
      (isLoggedIn && !loading && !isViewerDataLoading && !userEmail),
    isUserEmailLoading:
      loading || isViewerDataLoading || isApplicationPhonePaginationLoading,
    userFormData: {
      userEmail,
      userPhone,
    },
  };
};
